import { useEffect, useRef, useState, useCallback } from "react";
import {
  listAgents,
  createAgent,
  generateAgentKey,
  revokeAgentKey,
  patchAgent,
  deleteAgent,
  type AgentSummary,
  type GeneratedKey,
} from "./api/client";
import { toast } from "@/components/Toaster";
import { ConfirmModal } from "@/components/ConfirmModal";
import { CopyButton } from "@/components/CopyButton";
import "../admin.css";

// Fix 4: hoist RTF to module scope — constructed once, not once per call
const RTF = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function fmtRelative(iso: string | null): string {
  if (!iso) return "—";
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (Math.abs(sec) < 60) return RTF.format(-sec, "second");
  const min = Math.round(sec / 60);
  if (Math.abs(min) < 60) return RTF.format(-min, "minute");
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 24) return RTF.format(-hr, "hour");
  return RTF.format(-Math.round(hr / 24), "day");
}

export function ConnectedAgentsPage() {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [revealed, setRevealed] = useState<GeneratedKey | null>(null);
  const [deleting, setDeleting] = useState<AgentSummary | null>(null);

  // Fix 2: ref to focus the Done button when the reveal dialog opens
  const revealDoneRef = useRef<HTMLButtonElement>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    listAgents()
      .then(setAgents)
      .catch(() => toast.error("Failed to load agents"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Fix 6: shared close helper — dismiss reveal and then refresh so the table
  // updates only after the operator has had a chance to copy the key.
  const closeReveal = useCallback(() => {
    setRevealed(null);
    refresh();
  }, [refresh]);

  // Fix 2: focus the Done button and listen for Escape while the reveal dialog is open
  useEffect(() => {
    if (!revealed) return;
    // Defer focus so the button is mounted before we try to focus it
    const raf = requestAnimationFrame(() => {
      revealDoneRef.current?.focus();
    });
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeReveal();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
    };
  }, [revealed, closeReveal]);

  const onCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createAgent(newName.trim());
      setNewName("");
      refresh();
    } catch {
      toast.error("Could not create agent (name taken?)");
    }
  };

  const onGenerate = async (id: string) => {
    try {
      const key = await generateAgentKey(id);
      setRevealed(key);
      // Fix 6: do NOT refresh here — refresh happens when the reveal closes
    } catch {
      toast.error("Could not generate key");
    }
  };

  const onRevoke = async (agentId: string, keyId: string) => {
    try {
      await revokeAgentKey(agentId, keyId);
      refresh();
    } catch {
      toast.error("Could not revoke key");
    }
  };

  const onToggle = async (a: AgentSummary) => {
    try {
      const updated = await patchAgent(a.id, { status: a.status === "active" ? "disabled" : "active" });
      setAgents((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
    } catch {
      toast.error("Could not update agent");
    }
  };

  const onDelete = async () => {
    if (!deleting) return;
    try {
      await deleteAgent(deleting.id);
      setDeleting(null);
      refresh();
    } catch {
      // Fix 3: close the modal on failure so the operator can see the toast
      // and doesn't accidentally trigger a second delete call
      setDeleting(null);
      toast.error("Could not delete agent");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="admin-title">Connected Agents</h1>
      </div>

      {/* Register form */}
      <div
        style={{
          display: "flex",
          gap: "var(--space-2)",
          alignItems: "center",
          marginBottom: "var(--space-4)",
        }}
      >
        <input
          className="rb-input"
          style={{ flex: "0 1 280px" }}
          placeholder="New agent name…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void onCreate();
          }}
        />
        <button
          className="admin-btn"
          onClick={() => void onCreate()}
          disabled={!newName.trim()}
        >
          + Register agent
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="loading-spinner" />
        </div>
      ) : agents.length === 0 ? (
        <p className="rl-empty">No agents registered yet.</p>
      ) : (
        <table className="roster-table">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Status</th>
              <th>Version</th>
              <th>Last heartbeat</th>
              <th>Batches today</th>
              <th>Keys</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => {
              // Fix 5: compute active keys once per row
              const activeKeys = a.keys.filter((k) => k.status === "active");
              return (
                <tr key={a.id} className="roster-row">
                  <td style={{ fontWeight: 600 }}>{a.name}</td>
                  <td>
                    <span
                      className="jt-badge"
                      style={
                        a.status === "active"
                          ? { color: "var(--color-success)", borderColor: "var(--color-success)" }
                          : { color: "var(--color-error)", borderColor: "var(--color-error)" }
                      }
                    >
                      {a.status}
                    </span>
                  </td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}>
                    {a.agentVersion ?? "—"}
                  </td>
                  <td
                    style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)" }}
                    title={a.lastHeartbeatAt ? new Date(a.lastHeartbeatAt).toLocaleString() : undefined}
                  >
                    {fmtRelative(a.lastHeartbeatAt)}
                  </td>
                  <td
                    style={{
                      fontWeight: 600,
                      color: a.batchesToday > 0 ? "var(--color-primary)" : "var(--color-text-muted)",
                    }}
                  >
                    {a.batchesToday}
                  </td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
                      {activeKeys.map((k) => (
                        <div
                          key={k.id}
                          style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}
                        >
                          <code
                            style={{
                              fontSize: "var(--text-xs)",
                              fontFamily: "var(--font-mono)",
                              padding: "1px 4px",
                              background: "var(--color-bg-primary)",
                              border: "1px solid var(--color-border)",
                              borderRadius: "var(--radius-sm)",
                            }}
                          >
                            {k.keyPrefix}
                          </code>
                          <button
                            className="jt-action-btn"
                            onClick={() => void onRevoke(a.id, k.id)}
                          >
                            Revoke
                          </button>
                        </div>
                      ))}
                      {/* Fix 5: reuse activeKeys for the marginTop check */}
                      <button
                        className="admin-btn--secondary jt-action-btn"
                        style={{ marginTop: activeKeys.length > 0 ? "var(--space-1)" : 0 }}
                        onClick={() => void onGenerate(a.id)}
                      >
                        Generate key
                      </button>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "var(--space-1)" }}>
                      <button
                        className="jt-action-btn"
                        onClick={() => void onToggle(a)}
                      >
                        {a.status === "active" ? "Disable" : "Enable"}
                      </button>
                      <button
                        className="jt-action-btn jt-action-btn--danger"
                        onClick={() => setDeleting(a)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* One-time key reveal dialog */}
      {revealed && (
        <div
          className="dialog-overlay"
          onClick={closeReveal}
        >
          <div
            className="dialog-panel"
            role="dialog"
            aria-label="New agent key"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="dialog-title">New Agent Key</h2>
            <p
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--color-warning)",
                marginBottom: "var(--space-3)",
              }}
            >
              Copy this key now — you won't be able to see it again.
            </p>
            <code
              style={{
                display: "block",
                padding: "var(--space-3)",
                background: "var(--color-bg-primary)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--text-sm)",
                wordBreak: "break-all",
                marginBottom: "var(--space-4)",
                color: "var(--color-primary)",
              }}
            >
              {revealed.plaintext}
            </code>
            {/* Fix 1: use shared CopyButton — shows "✓ Copied" state, warns on failure */}
            <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
              <CopyButton value={revealed.plaintext} label="key" />
              {/* Fix 2: Done button receives focus on open (ref), and closeReveal also
                  handles Escape via the useEffect above */}
              <button
                ref={revealDoneRef}
                className="admin-btn"
                onClick={closeReveal}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      <ConfirmModal
        open={!!deleting}
        title="Delete agent?"
        description={
          deleting
            ? `"${deleting.name}" and all its keys will be permanently removed. This cannot be undone.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => void onDelete()}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
